import { useCallback, useEffect, useState } from 'react';

import { getCustomerApi } from '../../api/endpoints/customer';
import { useUser } from '../../context/authenticatedUser';
import {
  getRecentlyVisitedCustomers,
  RecentlyVisitedCustomer,
} from '../../utils/recentlyVisitedCustomers';

export const useRecentlyVisitedCustomers = () => {
  const user = useUser();
  const userId = user?.id;
  const [recentCustomers, setRecentCustomers] = useState<
    RecentlyVisitedCustomer[]
  >([]);

  const refresh = useCallback(async () => {
    const storedCustomers = getRecentlyVisitedCustomers(userId);
    const validatedCustomers = await Promise.all(
      storedCustomers.map(async (customer) => {
        try {
          const latestCustomer = await getCustomerApi(customer.customerId);
          if (
            !latestCustomer?.deleteBy ||
            new Date(latestCustomer.deleteBy).getTime() > Date.now()
          ) {
            return customer;
          }
        } catch (error) {
          // Skip customers that no longer exist or cannot be fetched.
        }

        return null;
      }),
    );
    setRecentCustomers(
      validatedCustomers.filter(
        (customer): customer is RecentlyVisitedCustomer => customer !== null,
      ),
    );
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setRecentCustomers([]);
      return;
    }

    refresh();

    const handleStorageChange = (e: StorageEvent) => {
      const userStorageKey = `admin_recently_visited_customers_${userId}`;
      if (e.key === userStorageKey) {
        refresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refresh, userId]);

  return { recentCustomers, refresh };
};
